import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AppCard,
  AppInput,
  AppSingleSelect,
  AppTextArea,
  AppFileUploader,
  AppCheckbox,
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
} from '@/layout/AppComponents';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../../components/helpers/Translator';
import { copyToClibpoard } from '../../../../../../minions/string';
import {
  identificationOptions,
  relationOptions,
  complaintOptions,
} from '../../constants/options';
import colors from '../../../../../../constants/colors';

export default function CommunicationPipelineUserReportAddCopisaTemplate(
  props
) {
  const { reportHex, onAdd, onFinish } = props;
  const conditionsFile = useSelector(
    (state) => state.currentPage?.moduleData?.conditionsFile
  );
  const [isCopied, setIsCopied] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
        nameSurname: Yup.string().when(
            ['identification'],
            ([identification], schema) => {
                if (identification === 1) {
                    return schema.required(ReactDOMServer.renderToString(<Translator file="module-userManagement" name="required" />));
                } else {
                    return schema.notRequired();
                }
            }
        ),
        email: Yup.string().when(
            ['identification'],
            ([identification], schema) => {
                if (identification === 1) {
                    return schema
                        .required(ReactDOMServer.renderToString(<Translator file="module-userManagement" name="required" />))
                        .email(ReactDOMServer.renderToString(<Translator file="module-userManagement" name="enter-valid-email" />));
                } else {
                    return schema.notRequired();
                }
            }
        ),
        relation: Yup.number()
            .nullable()
            .required(ReactDOMServer.renderToString(<Translator file="module-userManagement" name="required" />)),
        complaint: Yup.number().nullable(),
        description: Yup.string().required(
            ReactDOMServer.renderToString(<Translator file="module-userManagement" name="required" />)
        ),
        password: Yup.string().required(
            ReactDOMServer.renderToString(<Translator file="module-userManagement" name="required" />)
        ),
        acceptTerms: Yup.bool().oneOf(
            [!!conditionsFile],
            ReactDOMServer.renderToString(<Translator file="module-userManagement" name="required" />)
        ),
    })
  );

  const handleCopyClipboard = useCallback(() => {
    copyToClibpoard(reportHex);
    AppNotificationAlert({
      title: ReactDOMServer.renderToString(
        <Translator file="module-communicationPipeline" name="copied" />
      ),
      message: ReactDOMServer.renderToString(
        <Translator
          file="module-communicationPipeline"
          name="complaint-code-copied-clipboard"
        />
      ),
      type: 'success',
      duration: 5000,
      position: 'top-right',
    });
    setIsCopied(true);
  }, [reportHex]);

  return (
    <>
      <h5 className="mb-3">
        <Translator file="module-communicationPipeline" name="new-complaint" />
      </h5>
      <AppCard bodyClassName="minHeight600">
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={validationSchema}
          initialValues={{
            identification: 1,
            nameSurname: '',
            email: '',
            phone: '',
            relation: null,
            complaint: null,
            description: '',
            howKnown: '',
            files: [],
            password: '',
            acceptTerms: false,
          }}
          onSubmit={onAdd}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="alert alert-info">
                <Translator
                  file="module-communicationPipeline"
                  name="communication-anonymously-advise-regulations-conducted"
                  isHtml
                />
              </div>
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="identification"
                />
                <span className="text-danger">*</span>
              </h3>
              <AppSingleSelect
                placeholder={ReactDOMServer.renderToString(
                  <Translator
                    file="module-communicationPipeline"
                    name="select-how-want-submit-complaint"
                  />
                )}
                options={identificationOptions}
                onChange={(v) => setFieldValue('identification', v)}
                value={values.identification}
                required
              />
              {values.identification === 0 && (
                <Translator
                  file="module-communicationPipeline"
                  name="not-forget-password-resend-anonymous-box-asked-any-information"
                  isHtml
                />
              )}
              {values.identification === 1 && (
                <div className="user-info">
                  <h3 className="mt-0">
                    <Translator
                      file="module-communicationPipeline"
                      name="first-name-last-name"
                    />
                    <span className="text-danger">*</span>
                  </h3>
                  <AppInput
                    onChange={(v) => setFieldValue('nameSurname', v)}
                    value={values.nameSurname}
                    error={errors.nameSurname}
                  />
                  <h3>
                    <Translator
                      file="module-communicationPipeline"
                      name="email"
                    />
                    <span className="text-danger">*</span>
                  </h3>
                  <AppInput
                    onChange={(v) => setFieldValue('email', v.trim())}
                    value={values.email}
                    error={errors.email}
                  />
                  <h3>
                    <Translator
                      file="module-communicationPipeline"
                      name="phone"
                    />
                  </h3>
                  <AppInput
                    onChange={(v) => setFieldValue('phone', v.trim())}
                    value={values.phone}
                    error={errors.phone}
                  />
                </div>
              )}
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="identify-relationship-company-maintain-group-company"
                />
                <span className="text-danger">*</span>
              </h3>
              <AppSingleSelect
                options={relationOptions}
                onChange={(v) => setFieldValue('relation', v)}
                value={values.relation}
                error={errors.relation}
                required
              />
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="what-behavior-want-report"
                />
              </h3>
              <AppSingleSelect
                options={complaintOptions}
                onChange={(v) => setFieldValue('complaint', v)}
                value={values.complaint}
                error={errors.complaint}
              />
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="please-provide-detailed-description-possible-chronologically"
                />
                <span className="text-danger">*</span>
              </h3>
              <AppTextArea
                onChange={(v) => setFieldValue('description', v)}
                value={values.description}
                error={errors.description}
                resize="vertical"
                height="180px"
              />
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="describe-became-aware-reported-events"
                />
              </h3>
              <AppTextArea
                onChange={(v) => setFieldValue('howKnown', v)}
                value={values.howKnown}
                resize="vertical"
                height="180px"
              />
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="document-file-substantiates-reported-facts-upload-below"
                />
              </h3>
              {values.files.map((file, index) => (
                <div className="mt-2" key={index}>
                  <label
                    className="mb-2 pointer font-weight-light"
                    onClick={() =>
                      setFieldValue(
                        'files',
                        values?.files.filter((f, i) => i !== index)
                      )
                    }
                  >
                    <AppRemixIcon icon="delete-bin-6" color={colors.danger} />{' '}
                    {file?.name}
                  </label>
                </div>
              ))}
              <AppFileUploader
                onChange={(v) =>
                  setFieldValue('files', [...values.files, v?.[0]])
                }
                placeholder={<Translator file="general" name="select.a.file" />}
              />
              <h3>
                <Translator
                  file="module-communicationPipeline"
                  name="enter-password-tracking-complaint"
                />
                <span className="text-danger">*</span>
              </h3>
              <AppInput
                onChange={(v) => setFieldValue('password', v.trim())}
                value={values.password}
                error={errors.password}
                isPassword
              />
              {conditionsFile && (
                <span className="d-inline-block">
                  <AppCheckbox
                    onChange={(v) => setFieldValue('acceptTerms', v)}
                    value={values.acceptTerms}
                    error={errors.acceptTerms}
                    required
                  >
                    <span className={errors.acceptTerms ? 'text-danger' : ''}>
                      Los datos personales que nos facilite serán tratados por
                      Grupo Empresarial Copisa, S.A. responsable y gestora del
                      Sistema Interno de Información de Grupo Copisa, que será
                      quien se ocupe de la gestión e investigación que se
                      informen a través del canal ético. De acuerdo con lo
                      anterior, le informamos que los datos podrán ser tratados
                      con la finalidad de resolver su consulta, recibir y
                      gestionar las denuncias o alertas comunicadas y, si
                      procede, realizar la investigación correspondiente, así
                      como ofrecerle las garantías previstas en la Política del
                      Sistema de Información. Adicionalmente, podremos tratar
                      los datos para acreditar el buen funcionamiento de nuestro
                      modelo de cumplimiento, y para la defensa de los derechos
                      de la compañía. Puede ampliar la información sobre el
                      tratamiento de los datos y conocer los derechos que le
                      asisten en nuestra{' '}
                      <a
                        href={conditionsFile?.url}
                        target="_blank"
                        className={`text-decoration-underline ${
                          errors.acceptTerms ? 'text-danger' : ''
                        }`}
                      >
                        Política de privacidad y la Política del sistema interno
                        de Denuncias.
                      </a>
                    </span>
                  </AppCheckbox>
                </span>
              )}
              <div className="alert alert-warning mt-1">
                <Translator
                  file="module-communicationPipeline"
                  name="after-completing-submission-window-displaying-complaint-saved-correctly"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary d-block"
                disabled={isSubmitting}
              >
                <Translator
                  file="module-communicationPipeline"
                  name="send-to"
                />
              </button>
            </form>
          )}
        </Formik>
      </AppCard>
      <AppModal
        show={!!reportHex}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-communicationPipeline"
            name="complaint-code"
          />
        )}
        isCloseButton={false}
      >
        <ReportCodeContainer>
          <h3 className="mb-3 pointer" onClick={handleCopyClipboard}>
            <AppRemixIcon icon="file-copy" size={20} />
            {reportHex}
          </h3>
          <h6>
            <Translator
              file="module-communicationPipeline"
              name="your-complaint-code"
            />
          </h6>
          <h6>
            <Translator
              file="module-communicationPipeline"
              name="copy-access-your-complaint"
            />
          </h6>
          {isCopied && (
            <span
              className="btn btn-success btn-sm btn-block"
              onClick={onFinish}
            >
              <Translator file="module-communicationPipeline" name="complete" />
            </span>
          )}
        </ReportCodeContainer>
      </AppModal>
    </>
  );
}

const ReportCodeContainer = styled.div`
  text-align: center;
  h3 {
    font-size: 24px;
  }
`;
